// Client-side sidebar active-item highlighting. The sidebar HTML is rendered once per
// (section, version, language, variant) and cached, so the server no longer emits per-page
// `active` classes or an expanded collapse trail; this script applies them from the current
// location. It runs in the critical bundle so the highlight lands before first paint.
//
// Matching is exact first, nearest-ancestor second: a link whose path equals the current
// path always wins, and only when no link matches exactly does the longest link that is a
// path ancestor of the current page take over (flagged `aria-current="true"` rather than
// "page"). The ancestor pass is what keeps the trail highlighted for pages the menu cannot
// contain - a page built with `build.list = never`, or one generated by a content adapter,
// is absent from the page collections the menu is built from, so it has no entry of its own.
// Without the fallback nothing matched at all and the sidebar rendered with no active item
// and every group collapsed. Ancestor matching compares against `path + "/"`, so
// `/docs/guides` never claims `/docs/guides-advanced/`, and it skips links to the site root,
// which would otherwise claim every page on the site.
//
// Ancestor matching is a property of the link's href, so it needs no opt-in marker in the
// markup: every link in the nav is a candidate. Do not reintroduce one.
//
// Exactly one destination is marked, whether it is a leaf or a group title; a rail group
// renders its children twice (inline and in its flyout), so a destination can be marked in
// two places, of which only one is ever visible. Flyout links are copies and are never chosen as the match,
// so the trail is always resolved on the inline copy. The groups above it
// are expanded but not highlighted: they are the trail to the current page, not the current
// page, and marking them would leave a reader unable to tell which of the four rows lit up
// is the one they are on.
//
// A rail group's subpage also marks the group itself with `sidebar-group-trail`: in the
// icon-only rail the subpage's own row is hidden, and that class is how the group's icon
// carries the highlight instead.
(function () {
  'use strict'

  function normalize (path) {
    try { path = decodeURI(path) } catch { /* keep the raw path */ }
    if (path.length > 1) path = path.replace(/\/+$/, '')
    return path || '/'
  }

  // The normalized same-origin path a sidebar link points at, or null for a link that
  // cannot match (empty, a fragment, or another origin).
  function linkPath (link) {
    var href = link.getAttribute('href')
    if (!href || href.charAt(0) === '#') return null
    var url
    try { url = new URL(href, window.location.origin) } catch { return null }
    if (url.origin !== window.location.origin) return null
    return normalize(url.pathname)
  }

  function findBestMatch (nav, pathname) {
    var best = null
    var bestLength = -1
    var bestExact = false
    // The brand logo is inside the <nav> but is not a menu entry: it points at the site
    // home, so as an ancestor candidate it would match every page under it and light up
    // the logo whenever nothing better was found.
    nav.querySelectorAll('a[href]:not(.sidebar-brand)').forEach(function (link) {
      // Flyout links are copies of the inline ones and sit earlier in the DOM; choosing one
      // would leave the match outside its group's collapse, so the trail would not open.
      if (link.closest('.sidebar-group-flyout')) return
      var path = linkPath(link)
      if (path === null) return
      if (path === pathname) {
        if (!bestExact) {
          best = link
          bestLength = path.length
          bestExact = true
        }
      } else if (!bestExact && path !== '/') {
        // A link to the site root is an ancestor of every page, so it carries no locality:
        // honouring it would light up a "Home"/"Overview" entry on pages that sit outside
        // the menu entirely. Such a link still wins when it matches the path exactly.
        var prefix = path + '/'
        if (pathname.indexOf(prefix) === 0 && path.length > bestLength) {
          best = link
          bestLength = path.length
        }
      }
    })
    return { link: best, exact: bestExact, path: best ? linkPath(best) : null }
  }

  function initNav (nav, pathname) {
    var match = findBestMatch(nav, pathname)
    var link = match.link
    if (!link) return

    // A group title is not the row it heads: the row is the `.sidebar-item-group` around it,
    // which also spans the collapse chevron beside the title. Marking only the anchor leaves
    // nothing for the stylesheet to give an active group the treatment an active leaf gets,
    // because a leaf *is* its own row. So the class goes on both - the anchor for the text,
    // the row for its surface - while `aria-current` stays on the link it describes.
    var group = link.closest('.sidebar-item-group')

    nav.classList.add('sidebar-no-transition')
    // A rail group renders its children twice - inline and in its flyout - so the
    // destination is marked in both copies; only one is ever visible. Extra copies are
    // marked only inside a rail group, so a sidebar without `groups` still marks just the
    // matched link, as it did before rail groups existed.
    nav.querySelectorAll('a[href]:not(.sidebar-brand)').forEach(function (el) {
      if (linkPath(el) !== match.path) return
      if (el !== link && !el.closest('.sidebar-group')) return
      el.classList.add('active')
      el.setAttribute('aria-current', match.exact ? 'page' : 'true')
    })
    if (group) group.classList.add('active')

    // In the icon-only rail a subpage's row is hidden, so its group's icon carries the
    // highlight (components/_sidebar.scss styles the class only in that state).
    var railGroup = link.closest('.sidebar-group')
    var isRailTitle = link.hasAttribute('data-sidebar-group-toggle')
    if (railGroup && !isRailTitle) railGroup.classList.add('sidebar-group-trail')

    // Expand the collapse trail: the matched group's own collapse (when the link is a group
    // title) plus every collapse ancestor within this nav instance.
    var own = null
    if (group && group.parentElement) {
      own = group.parentElement.querySelector(':scope > .collapse')
    } else if (railGroup && isRailTitle) {
      own = railGroup.querySelector(':scope > .collapse')
    }
    var trail = own ? [own] : []
    var ancestor = link.closest('.collapse')
    while (ancestor && nav.contains(ancestor)) {
      trail.push(ancestor)
      ancestor = ancestor.parentElement && ancestor.parentElement.closest('.collapse')
    }
    trail.forEach(function (el) {
      el.classList.add('show')
      if (el.id) {
        nav.querySelectorAll('[data-bs-target="#' + el.id + '"]').forEach(function (toggle) {
          toggle.setAttribute('aria-expanded', 'true')
        })
      }
    })
    nav.offsetHeight // force reflow before re-enabling transitions
    nav.classList.remove('sidebar-no-transition')

    // Clicking the current group's title toggles its collapse instead of re-navigating.
    if (own && match.exact) {
      link.addEventListener('click', function (event) {
        if (nav.classList.contains('sidebar-collapsed')) return
        if (typeof bootstrap === 'undefined' || !bootstrap.Collapse) return
        event.preventDefault()
        bootstrap.Collapse.getOrCreateInstance(own).toggle()
      })
    }
  }

  function init () {
    var pathname = normalize(window.location.pathname)
    document.querySelectorAll('[data-sidebar-nav]').forEach(function (nav) {
      initNav(nav, pathname)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
}())
